/**
 * @typedef \{\{
 *    id: string;
 *    title: string;
 *    appliesOnOneTimePurchase: boolean;
 *    value: {
 *      percentage: number;
 *    };
 *    appliesTo: "collections";
 *    collections: {
 *      title: string;
 *      id: string;
 *    }[];
 *    products: [];
 * }} Discount
 */
class UncsProductPrice extends HTMLElement {

  constructor() {
    super();
    this._formatter = null;
    console.log("UncsProductPrice constructor");
  }

  /**
   * @returns {Promise<void>}
   */
  async connectedCallback() {
    if (!this.formatter) return;

    const discountsService = UncsDiscountsService.getInstance();
    const discounts = await discountsService.getDiscounts();

    const matchingDiscounts = this._findMatchingDiscounts(discounts);
    if (matchingDiscounts.length === 0) {
      console.log("Did not find any matching discounts.");
      return;
    }

    console.log("Matching discounts: ", matchingDiscounts);

    const updatedPrice = this._applyDiscounts(this.price, matchingDiscounts);

    this._setDiscountHtml(this.price, updatedPrice, matchingDiscounts);
  }

  /**
   * @param {Discount[]} discounts
   * @returns {Discount[]}
   */
  _findMatchingDiscounts(discounts) {
    /** @type {Discount[]} */
    const matching = [];

    for (const discount of discounts) {
      if (discount.appliesTo === 'collections') {
        const collections = discount.collections.map((collection) => collection.id);
        const hasMatchingCollection = this.collectionsGids.some((gid) => collections.includes(gid));

        if (hasMatchingCollection) {
          matching.push(discount);
          continue;
        }
      }
    }

    return matching;
  }

  /**
   * @param {number} price
   * @param {Discount[]} discounts
   * @returns {number}
   */
  _applyDiscounts(price, discounts) {
    const totalPercentDiscount = discounts.reduce((acc, discount) => acc + discount.value.percentage, 0.0);
    const pricePercentDifference = Math.max(1 - totalPercentDiscount, 0);

    return parseFloat((price * pricePercentDifference).toFixed(2));
  }

  /**
   * @param {number} originalPrice
   * @param {number} discountedPrice
   * @param {Discount[]} appliedDiscounts
   */
  _setDiscountHtml(originalPrice, discountedPrice, appliedDiscounts) {
    const formattedOriginalPrice = this.formatter.format(originalPrice);
    const formattedDiscountedPrice = this.formatter.format(discountedPrice);
    const appliedDiscountsHtml = appliedDiscounts
      .map(
        ({ title }) => `
        <li class="discounts__discount uncs__discount">
          <span class="svg-wrapper">
            <svg class="icon icon-discount" viewBox="0 0 12 12">
              <path fill="currentColor" fill-rule="evenodd" d="M7 0h3a2 2 0 0 1 2 2v3a1 1 0 0 1-.3.7l-6 6a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4l6-6A1 1 0 0 1 7 0m2 2a1 1 0 1 0 2 0 1 1 0 0 0-2 0" clip-rule="evenodd"></path>
            </svg>
          </span>
          <span class="uncs__discount-label">
            ${title}
          </span>
        </li>
    `
      )
      .join('');

    this.classList.add('price--on-sale');
    this.priceElements.forEach((el) => {
      el.parentElement.innerHTML = `
        <span class="visually-hidden visually-hidden--inline">Normál ár</span>
        <span>
          <s class="price-item price-item--regular">
            ${formattedOriginalPrice}
          </s>
        </span>
        <span class="visually-hidden visually-hidden--inline">Akciós ár</span>
        <span class="price-item price-item--sale price-item--last">
          ${formattedDiscountedPrice}
        </span>
        <ul class="discounts list-unstyled uncs__discounts" role="list" aria-label="Discount">
          ${appliedDiscountsHtml}
        </ul>
      `;
    });
  }

  /**
   * @returns {Intl.NumberFormat|null}
   */
  get formatter() {
    if (this._formatter) {
      return this._formatter;
    }

    console.log("Creating formatter", this.currency, this);
    if (this.currency === 'CZK') {
      this._formatter = new Intl.NumberFormat('cs-CZ', {
        currency: 'CZK',
        style: 'currency',
        maximumFractionDigits: 0,
      });
    } else if (this.currency === 'HUF') {
      this._formatter = new Intl.NumberFormat('hu-HU', {
        currency: 'HUF',
        style: 'currency',
        maximumFractionDigits: 3,
      });
    } else if (this.currency === 'USD') {
      this._formatter = new Intl.NumberFormat('en-US', {
        currency: 'USD',
        style: 'currency',
        maximumFractionDigits: 2,
      });
    } else if (this.currency === 'EUR') {
      this._formatter = new Intl.NumberFormat(undefined, {
        currency: 'EUR',
        style: 'currency',
        maximumFractionDigits: 2,
      });
    }

    return this._formatter;
  }

  /**
   * @returns {number}
   */
  get price() {
    return parseFloat(this.dataset.price) / 100;
  }

  /**
   * @returns {string}
   */
  get currency() {
    return this.dataset.currency.toString();
  }

  /**
   * @returns {NodeListOf<HTMLSpanElement>}
   */
  get priceElements() {
    return this.querySelectorAll("[data-target='price']");
  }

  /**
   * @returns {HTMLDivElement}
   */
  get priceContainerElement() {
    return this.querySelector("[data-target='priceContainer']");
  }

  /**
   * @param {string|number}
   */
  set formattedPrice(newPrice) {
    this.priceElements.forEach((el) => (el.innerHTML = this.formatter.format(parseFloat(newPrice))));
  }

  /**
   * @returns {number}
   */
  get productId() {
    return parseInt(this.dataset.productId);
  }

  /**
   * @returns {string}
   */
  get productGid() {
    return `gid://shopify/Product/${this.productId}`;
  }

  /**
   * @returns {number[]}
   */
  get collectionsIds() {
    return JSON.parse(this.dataset.collectionsIds);
  }

  /**
   * @returns {string[]}
   */
  get collectionsGids() {
    return this.collectionsIds.map((id) => `gid://shopify/Collection/${id}`);
  }
}

customElements.define('uncs-product-price', UncsProductPrice);
